/**
 * 작성자 : 한영광
 * 날짜 : 2025.01.26
 * 기능 : 화재 감지 피드백 작성 가이드 모달
 */

import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalBackground, Modal } from '../../../assets/styles/Modal';
import { FALSE_POSITIVE_CAUSES, FALSE_NEGATIVE_CAUSES } from './feedbackExamples';

interface IProps {
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  openModal: IModal;
}

interface IModal {
  status: boolean;
  type: string;
}

const FeedbackGuideModal = ({ setOpenModal, openModal }: IProps) => {
  const { t } = useTranslation();

  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  };

  return (
    <ModalBackground onClick={onClickClose} role='presentation' style={{ zIndex: 9999 }}>
      <Modal>
        <div
          className='modal height-fit signup'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
          style={{ width: '90vw', maxWidth: '1200px', maxHeight: '90vh' }}
        >
          <div className='inputForm-head'>
            {openModal.type === 'FALSE_POSITIVE' && <div className='modalHeaderTitle'>{t('오탐지 원인 카테고리')}</div>}
            {openModal.type === 'FALSE_NEGATIVE' && <div className='modalHeaderTitle'>{t('미탐지 원인 카테고리')}</div>}
            <div className='closeBtn' onClick={onClickClose} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>

          <div className='black flexColumn col-w100' style={{ overflow: 'auto', padding: '20px' }}>
            {/* 오탐지 원인 카테고리 */}
            {openModal.type === 'FALSE_POSITIVE' && (
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  {Object.entries(FALSE_POSITIVE_CAUSES).map(([key, cause]) => (
                    <div key={key} style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <h4 style={{ color: '#495057', marginBottom: '8px', fontSize: '16px' }}>{cause.code}</h4>
                      <p style={{ color: '#6c757d', marginBottom: '10px', fontSize: '14px' }}>{cause.description}</p>
                      <div style={{ fontSize: '12px', color: '#868e96' }}>
                        <strong>예시:</strong> {cause.examples.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 미탐지 원인 카테고리 */}
            {openModal.type === 'FALSE_NEGATIVE' && (
              <div style={{ marginBottom: '30px' }}>
                {/* 화재 크기/규모 */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#495057', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>{FALSE_NEGATIVE_CAUSES.FIRE_SIZE.label}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                    {Object.entries(FALSE_NEGATIVE_CAUSES.FIRE_SIZE)
                      .filter(([key]) => !['required', 'label'].includes(key))
                      .map(([key, cause]) => {
                        const causeItem = cause as { code: string; description: string; examples: string[] };
                        return (
                          <div key={key} style={{ backgroundColor: '#e8f5e8', padding: '12px', borderRadius: '6px', border: '1px solid #c3e6c3' }}>
                            <h5 style={{ color: '#155724', marginBottom: '5px', fontSize: '14px' }}>{causeItem.code}</h5>
                            <p style={{ color: '#155724', marginBottom: '8px', fontSize: '12px' }}>{causeItem.description}</p>
                            <div style={{ fontSize: '11px', color: '#2d5016' }}>
                              <strong>예시:</strong> {causeItem.examples.join(', ')}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* 화재 종류 */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#495057', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>{FALSE_NEGATIVE_CAUSES.FIRE_TYPE.label}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                    {Object.entries(FALSE_NEGATIVE_CAUSES.FIRE_TYPE)
                      .filter(([key]) => !['required', 'label'].includes(key))
                      .map(([key, cause]) => {
                        const causeItem = cause as { code: string; description: string; examples: string[] };
                        return (
                          <div key={key} style={{ backgroundColor: '#e8f5e8', padding: '12px', borderRadius: '6px', border: '1px solid #c3e6c3' }}>
                            <h5 style={{ color: '#155724', marginBottom: '5px', fontSize: '14px' }}>{causeItem.code}</h5>
                            <p style={{ color: '#155724', marginBottom: '8px', fontSize: '12px' }}>{causeItem.description}</p>
                            <div style={{ fontSize: '11px', color: '#2d5016' }}>
                              <strong>예시:</strong> {causeItem.examples.join(', ')}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* 감지 실패 주요 원인 */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#495057', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>{FALSE_NEGATIVE_CAUSES.DETECTION_FAILURE_CAUSE.label}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
                    {Object.entries(FALSE_NEGATIVE_CAUSES.DETECTION_FAILURE_CAUSE)
                      .filter(([key]) => !['required', 'label'].includes(key))
                      .map(([key, cause]) => {
                        const causeItem = cause as { code: string; description: string; examples: string[] };
                        return (
                          <div key={key} style={{ backgroundColor: '#e8f5e8', padding: '12px', borderRadius: '6px', border: '1px solid #c3e6c3' }}>
                            <h5 style={{ color: '#155724', marginBottom: '5px', fontSize: '14px' }}>{causeItem.code}</h5>
                            <p style={{ color: '#155724', marginBottom: '8px', fontSize: '12px' }}>{causeItem.description}</p>
                            <div style={{ fontSize: '11px', color: '#2d5016' }}>
                              <strong>예시:</strong> {causeItem.examples.join(', ')}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </ModalBackground>
  );
};

export default FeedbackGuideModal;
